import { tWithKeyAttr as t } from "../../../i18nHelper";
import React, {
  Dispatch,
  SetStateAction,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import styles from "./dashboardCheckBox.module.css";
import { TOption } from "../../../services/types/utils.types";
import i18n from "../../../i18n";

const DashboardCheckboxDropdown: React.FC<{
  options: TOption[];
  title: string;
  selectedValues: number[];
  onChange: Dispatch<SetStateAction<number[]>>;
}> = ({ options, title, selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const lang = i18n.language;
  const dir = useMemo(() => {
    return lang === "he" || lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCheckboxChange = (value: number) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter((v) => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handlecancelSelect = () => {
    onChange([]);
  };

  // Open dropdown when input is focused or typed
  const handleInputFocus = () => setIsOpen(true);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Show selected labels as placeholder if searchTerm is empty
  const selectedLabels =
    options
      .filter((opt) => selectedValues.includes(Number(opt.value)))
      .map((opt) => opt.label)
      .slice(0, 5)
      .join(", ") + (selectedValues.length > 5 ? " ..." : "");

  return (
    <div
      style={{ position: "relative", display: "inline-block" }}
      ref={dropdownRef}
    >
      <div className={styles.comboBoxTitleContainer}>
        <p className={styles.comboBoxTitle}>{title}</p>
        {filteredOptions.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: "4px",
              alignItems: "center",
              position: "absolute",
              top: 13,
              ...(dir === "rtl" ? { left: 8 } : { right: 8 }),
            }}
          >
            <button
              className={styles.selectAllIconButton}
              title={t('SELECT_ALL_1')}
              onClick={() => {
                const allFilteredValues = filteredOptions.map((opt) =>
                  Number(opt.value)
                );
                const newSelected = Array.from(new Set([...allFilteredValues]));
                onChange(newSelected);
              }}
            >
              <span className={styles.selectAllIconCircle}>
                {/* Checkmark SVG */}
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M5 10.5L9 14.5L15 7.5"
                    stroke="#1976d2"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
            <button
              className={styles.selectAllIconButton}
              title={t('CLEAN_THE_SELECTION')}
              type="button"
              onClick={() => {
                handlecancelSelect();
                setIsOpen(false);
                setSearchTerm("");
              }}
            >
              <span className={styles.selectAllIcon}>
                {/* X SVG */}
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M6 6L14 14M14 6L6 14"
                    stroke="#0066cc"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </button>
          </div>
        )}
      </div>
      <div className={styles.comboBox}>
        <input
          type="text"
          value={searchTerm}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          placeholder={selectedLabels || t("SELECT")}
          className={styles.searchInput}
        />
        <span style={{ fontSize: "14px" }}>▾</span>
      </div>

            {isOpen && (
                <div
                    style={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        width: "100%",
                        backgroundColor: "rgb(247, 248, 248)",
                        border: "1px solid rgba(9, 143, 239, 1)",
                        borderRadius: "6px",
                        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                        zIndex: 9999,
                        maxHeight: "250px",
                        overflowY: "auto"
                    }}
                >

                    {filteredOptions.length !== 0 ? (
                        filteredOptions.map((option) => (
                            <label
                                key={option.value}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    padding: "10px",
                                    fontWeight: "bold",
                                    fontSize: "15px",
                                    color: "rgba(9, 143, 239, 1)",
                                    direction: "rtl",
                                    cursor: "pointer"
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedValues.includes(Number(option.value))}
                                    onChange={() => handleCheckboxChange(Number(option.value))}
                                    style={{
                                        accentColor: "rgba(0, 67, 206, 1)",
                                        width: "16px",
                                        height: "16px"
                                    }}
                                />
                                {option.label}
                            </label>
                        ))
                    ) : (
                        <div className={styles.noResults}>
                            {t('NO_RESULTS_FOUND')}</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DashboardCheckboxDropdown;
